interface NetworkSVGProps {
  color?: string;
}

const NetworkSVG: React.FC<NetworkSVGProps> = ({ color = "#4F46E5" }) => {
  return (
    <svg
      viewBox="0 0 400 300"
      className="w-full h-full transform transition-transform duration-300 rounded-lg"
    >
      <defs>
        <linearGradient
          id="network-gradient"
          x1="0%"
          y1="0%"
          x2="100%"
          y2="100%"
        >
          <stop offset="0%" stopColor={color} stopOpacity="0.1" />
          <stop offset="100%" stopColor={color} stopOpacity="0.05" />
        </linearGradient>
        <filter id="network-glow">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      <style>
        {`
          @keyframes flowParticle {
            0% { offset-distance: 0%; }
            100% { offset-distance: 100%; }
          }
          @keyframes pulseNode {
            0% { transform: scale(1); opacity: 0.6; }
            50% { transform: scale(1.03); opacity: 0.7; }
            100% { transform: scale(1); opacity: 0.6; }
          }
          @keyframes floatNode {
            0% { transform: translateY(0px); }
            50% { transform: translateY(-2px); }
            100% { transform: translateY(0px); }
          }
          .flow-particle {
            animation: flowParticle 6s linear infinite;
          }
          .pulse-node {
            animation: pulseNode 8s ease-in-out infinite;
          }
          .float-node {
            animation: floatNode 10s ease-in-out infinite;
          }
        `}
      </style>

      {/* Background */}
      <rect width="400" height="300" fill="url(#network-gradient)" />

      {/* Network Nodes and Connections */}
      <g className="transition-colors duration-500">
        {/* Define node positions */}
        {[
          { x: 200, y: 150, size: 20 }, // Center
          { x: 120, y: 100, size: 15 }, // Top left
          { x: 280, y: 100, size: 15 }, // Top right
          { x: 100, y: 200, size: 15 }, // Bottom left
          { x: 300, y: 200, size: 15 }, // Bottom right
          { x: 200, y: 80, size: 12 }, // Top
          { x: 200, y: 220, size: 12 }, // Bottom
        ].map((node, i) => (
          <g
            key={i}
            className={`float-node`}
            style={{ animationDelay: `${i * 1.5}s` }}
          >
            {/* Node glow */}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.size + 5}
              fill={color}
              opacity="0.1"
              className="transition-colors duration-500"
            />
            {/* Node core */}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.size}
              fill={color}
              opacity="0.2"
              className="pulse-node transition-colors duration-500"
              style={{ animationDelay: `${i * 0.8}s` }}
            />
            {/* Node center */}
            <circle
              cx={node.x}
              cy={node.y}
              r={node.size * 0.6}
              fill={color}
              opacity="0.3"
              className="transition-colors duration-500"
            />
          </g>
        ))}

        {/* Connection lines with flowing particles */}
        {[
          "M200,150 L120,100",
          "M200,150 L280,100",
          "M200,150 L100,200",
          "M200,150 L300,200",
          "M200,150 L200,80",
          "M200,150 L200,220",
          "M120,100 L280,100",
          "M100,200 L300,200",
        ].map((path, i) => (
          <g key={i}>
            {/* Base connection line */}
            <path
              d={path}
              stroke={color}
              strokeWidth="2"
              opacity="0.15"
              fill="none"
              className="transition-colors duration-500"
            />
            {/* Flowing particles */}
            {[...Array(2)].map((_, j) => (
              <circle
                key={j}
                r="2.5"
                fill={color}
                opacity="0.4"
                className="flow-particle transition-colors duration-500"
                style={{
                  offsetPath: `path('${path}')`,
                  animationDelay: `${i * 0.5 + j * 2}s`,
                }}
              />
            ))}
          </g>
        ))}
      </g>

      {/* Decorative elements */}
      <g className="pulse-node">
        {[0, 120, 240].map((angle, i) => (
          <path
            key={i}
            d={`M${200 + Math.cos((angle * Math.PI) / 180) * 120} ${
              150 + Math.sin((angle * Math.PI) / 180) * 120
            } A 120 120 0 0 1 ${
              200 + Math.cos(((angle + 60) * Math.PI) / 180) * 120
            } ${150 + Math.sin(((angle + 60) * Math.PI) / 180) * 120}`}
            fill="none"
            stroke={color}
            strokeWidth="1"
            opacity="0.1"
            strokeDasharray="4,4"
            className="transition-colors duration-500"
          />
        ))}
      </g>
    </svg>
  );
};

export default NetworkSVG;
